import React, { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import AboutMenu from "@/components/AboutMenu";
import AdmissionInquiryModal from "@/pages/public/polyrotegaon/AdmissionInquiryModal";
import {
  Menu, X, Phone, Mail, MapPin, ArrowUpRight,
  Facebook, Instagram, Youtube, Linkedin,
} from "lucide-react";

const MitcidcoContext = createContext(null);
export const useMitcidco = () => useContext(MitcidcoContext);

// Distinct palette: deep maroon + royal gold + warm cream.
export const PALETTE = {
  primary: "#7C2D12",
  primaryDark: "#5B1A0A",
  accent: "#D4A017",
  accentDark: "#B58007",
  cream: "#FDF6E3",
  ink: "#1F1109",
};

export const NAV = [
  { to: "", end: true, label: "Home" },
  { to: "about", label: "About", isAboutMenu: true },
  { to: "trades", label: "Trades" },
  { to: "facilities", label: "Facilities" },
  { to: "activities", label: "Activities" },
  { to: "gallery", label: "Gallery" },
  { to: "careers", label: "Careers" },
  { to: "contact", label: "Contact" },
];

const ABOUT_ITEMS = [
  { to: "about", label: "About Us" },
  { to: "leadership", label: "Leadership Message" },
  { to: "executive-body", label: "Executive Body" },
];

export default function MitcidcoLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/website/mititicidco");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Website not found");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><div className="text-slate-500 font-serif text-xl">Loading…</div></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-slate-800">Website not found</h1>
          <p className="mt-2 text-slate-500">{err}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute;
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  const linkClass = ({ isActive }) =>
    `text-[13px] px-4 py-2.5 font-semibold uppercase tracking-wider whitespace-nowrap transition-all ${
      isActive
        ? "bg-[#D4A017] text-[#5B1A0A]"
        : "text-white/85 hover:bg-white/10 hover:text-white"
    }`;

  return (
    <MitcidcoContext.Provider value={data}>
      <Helmet prioritizeSeoTags>
        <title>Marathwada Pvt. ITI · CIDCO, Chhatrapati Sambhajinagar</title>
        <meta name="description" content="Marathwada Private Industrial Training Institute — CIDCO N-4, Chhatrapati Sambhajinagar. DVET & NCVT affiliated ITI offering Electrician and Mechanic Diesel trades since 2017." />
        <meta name="keywords" content="Marathwada ITI CIDCO, ITI Chhatrapati Sambhajinagar, ITI Aurangabad, Electrician course, Mechanic Diesel, DVET, NCVT, GS Mandal, MIT Campus CIDCO" />
        <link rel="canonical" href="https://gsmandal.org/mititicidco" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-[#FDF6E3]">
        {/* Top utility bar */}
        <div className="bg-[#5B1A0A] text-amber-50/90 text-[11px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              {inst.admission_phone && (
                <a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} data-testid="mitcidco-top-phone" className="inline-flex items-center gap-1 hover:text-[#D4A017]"><Phone size={11} className="text-[#D4A017]" />{inst.admission_phone}</a>
              )}
              {inst.email && (
                <a href={`mailto:${inst.email}`} data-testid="mitcidco-top-email" className="hidden md:inline-flex items-center gap-1 hover:text-[#D4A017]"><Mail size={11} className="text-[#D4A017]" />{inst.email}</a>
              )}
              <span className="hidden lg:inline-flex items-center gap-1"><MapPin size={11} className="text-[#D4A017]" />MIT Campus, CIDCO N-4, Chhatrapati Sambhajinagar</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <InstituteSwitcher currentSlug="mititicidco" testidPrefix="mitcidco-top-switcher" />
              {inst.facebook_url && (<a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mitcidco-top-facebook" className="hover:text-[#D4A017]"><Facebook size={13} /></a>)}
              {inst.instagram_url && (<a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mitcidco-top-instagram" className="hover:text-[#D4A017]"><Instagram size={13} /></a>)}
              {inst.linkedin_url && (<a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="mitcidco-top-linkedin" className="hover:text-[#D4A017]"><Linkedin size={13} /></a>)}
              {inst.youtube_url && (<a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="mitcidco-top-youtube" className="hover:text-[#D4A017]"><Youtube size={13} /></a>)}
              <NavLink to="fees" data-testid="mitcidco-top-know-fees" className="inline-flex items-center gap-1 bg-[#FDF6E3] hover:bg-white text-[#5B1A0A] px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold">Know Your Fees</NavLink>
            </div>
          </div>
        </div>

        {/* Logo bar */}
        <div className="bg-white border-b-4 border-[#D4A017]">
          <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] items-center gap-4">
            <NavLink to="" end className="flex items-center justify-start shrink-0" data-testid="mitcidco-logo-link">
              <img src={logoSrc} alt={inst.name} className="h-16 md:h-20 w-auto object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-[#B45309] font-bold">G.S. Mandal Chhatrapati Sambhajinagar</div>
              <div className="font-serif font-black text-lg md:text-2xl text-[#7C2D12] mt-1 leading-tight" data-testid="mitcidco-header-name">
                MARATHWADA PRIVATE INDUSTRIAL TRAINING INSTITUTE
              </div>
              <div className="font-serif text-sm md:text-base text-[#5B1A0A] mt-0.5 italic">CIDCO N-4 · Chhatrapati Sambhajinagar · Est. 2017</div>
              <div className="text-[10px] md:text-[11px] tracking-[0.12em] uppercase text-slate-600 mt-1">
                DVET Code · 272515101501 &nbsp;·&nbsp; NCVT MIS · PU27000880
              </div>
            </div>
            <div className="hidden md:flex flex-col items-end gap-1">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#7C2D12] font-bold">DVET &amp; DGT Affiliated</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500">NCVT · New Delhi</div>
              <button type="button" onClick={() => setInquiryOpen(true)} className="mt-1 inline-flex items-center gap-1.5 bg-[#7C2D12] hover:bg-[#5B1A0A] text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1.5">Admission Enquiry <ArrowUpRight size={10} strokeWidth={2.5} /></button>
            </div>
          </div>
        </div>

        {/* Main nav */}
        <nav className="sticky top-0 z-40 bg-[#7C2D12] shadow-md" data-testid="mitcidco-nav">
          <div className="max-w-7xl mx-auto px-2">
            <div className="hidden lg:flex items-center gap-0 py-0 flex-wrap">
              {NAV.map((n) => (
                n.isAboutMenu ? (
                  <AboutMenu
                    key="about"
                    linkClass={linkClass}
                    testidPrefix="mitcidco-nav-about"
                    menuClassName="bg-[#7C2D12] text-white ring-1 ring-white/10"
                    itemHoverClassName="hover:bg-white/10"
                    items={ABOUT_ITEMS}
                  />
                ) : (
                  <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mitcidco-nav-${n.to || "home"}`} className={linkClass}>
                    {n.label}
                  </NavLink>
                )
              ))}
            </div>
            <div className="lg:hidden flex items-center justify-between py-2 px-2">
              <span className="text-white text-[11px] tracking-[0.25em] uppercase font-bold">Menu</span>
              <button onClick={() => setMobileOpen((v) => !v)} className="p-2 text-white" aria-label="Menu" data-testid="mitcidco-nav-toggle">{mobileOpen ? <X /> : <Menu />}</button>
            </div>
            {mobileOpen && (
              <div className="lg:hidden pb-3 flex flex-col">
                {NAV.map((n) => (
                  n.isAboutMenu ? (
                    <React.Fragment key="about-mob">
                      {ABOUT_ITEMS.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          end={it.to === "about"}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mitcidco-nav-mob-${it.to}`}
                          className={linkClass}
                        >
                          {it.label}
                        </NavLink>
                      ))}
                    </React.Fragment>
                  ) : (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)} className={linkClass}>{n.label}</NavLink>
                  )
                ))}
              </div>
            )}
          </div>
        </nav>

        <main className="flex-1 bg-[#FDF6E3]"><Outlet /></main>

        {/* Footer */}
        <footer className="bg-[#5B1A0A] text-amber-50/80">
          <div className="max-w-7xl mx-auto px-5 py-14 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3">
                <img src={logoSrc} alt={inst.name} className="h-14 w-auto object-contain bg-white p-1.5 rounded" />
                <div>
                  <div className="text-white font-serif text-lg leading-tight">Marathwada Pvt. ITI · CIDCO</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A017] font-bold mt-1">A G.S. Mandal Institution</div>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed max-w-md">
                Established in 2017 by Gramaudyogik Shikshan Mandal, our institute delivers government-recognised vocational training at CIDCO N-4, Chhatrapati Sambhajinagar. Over 600 alumni serve leading companies and government establishments today.
              </p>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4A017] font-bold mb-3">Quick Links</div>
              <ul className="space-y-1.5 text-sm">
                {NAV.map((n) => <li key={n.to}><NavLink to={n.to} end={n.end} className="hover:text-white">{n.label}</NavLink></li>)}
                <li><Link to="fees" className="hover:text-white">Know Your Fees</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4A017] font-bold mb-3">Contact</div>
              <div className="text-sm space-y-2 leading-relaxed">
                <div className="flex items-start gap-2"><MapPin size={13} className="text-[#D4A017] mt-0.5 shrink-0" /><span>Plot No. 37, MIT Campus, CIDCO N-4, Chhatrapati Sambhajinagar, Maharashtra — 431003</span></div>
                {inst.admission_phone && <div className="flex items-center gap-2"><Phone size={13} className="text-[#D4A017]" /><a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} className="hover:text-white">{inst.admission_phone}</a></div>}
                {inst.email && <div className="flex items-center gap-2"><Mail size={13} className="text-[#D4A017]" /><a href={`mailto:${inst.email}`} className="hover:text-white break-all">{inst.email}</a></div>}
              </div>
              <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-slate-400">DVET · 272515101501 &nbsp;·&nbsp; NCVT · PU27000880</div>
            </div>
          </div>
          <div className="border-t border-amber-50/10">
            <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between flex-wrap gap-2 text-[11px] text-amber-50/60">
              <div>© {new Date().getFullYear()} Marathwada Private ITI, CIDCO · G.S. Mandal, Chhatrapati Sambhajinagar. All rights reserved.</div>
              <div className="flex items-center gap-1">Website powered by <span className="text-[#D4A017] font-semibold">GS Mandal CMS</span></div>
            </div>
          </div>
        </footer>
      </div>

      <AdmissionInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} slug="mititicidco" departments={data?.departments || []} />
    </MitcidcoContext.Provider>
  );
}
